import React, { useContext, useEffect, useReducer, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  VERIFICATION_TYPE_QUERY,
  ALL_VERIFICATION_TYPES_QUERY,
  ALL_KIND_OF_VERIFICATIONS_QUERY,
  ALL_REPORTS_QUERY,
  VERIFICATION_VALIDITY_TYPES_QUERY
} from '../queries';
import { PARTNER_CONSENTS } from '../../../../partnerConsents/queries';
import { UPDATE_VERIFICATION_TYPE } from '../mutations';
import { VERIFICATION_TYPE_UPDATED } from '../subscriptions';
import { FakeText, IconButton, Card } from '@totalsoft/rocket-ui';
import { reducer, initialState } from '../../../reducers/containerReducer';
import Details from '@mui/icons-material/PlaylistAdd';
import VerificationTypeDetailComponent from './VerificationTypeDetailComponent';
import { EXTERNAL_INTEROGATION_REPORT, REPORT, EXTERNAL_INTEROGATION, ValidityTypeNumberOfDays } from '../../../constants';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { shape, required, fromModel, when } from '@totalsoft/pure-validations';
import { useToast } from 'hooks/toasts/useToast';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import VerificationTypeUserGroupsListComponent from './VerificationTypeUserGroupsListComponent';
import { Origination } from 'constants/contexts';
import { ServiceContext } from 'providers/ServiceProvider';

const validator = fromModel(model =>
  shape({
    verificationName: required,
    kindOfVerificationId: required,
    validityTypeId: required,
    parentVerificationTypeId: required |> when(model.triggersVerifications === true)
  })
);

const VerificationTypeContainer = ({ verificationTypeId, onChangeTitle, openVerificationTest, onApplicabilityFormulaForTestChanged }) => {
  const [activeService] = useContext(ServiceContext);
  const [t] = useTranslation();
  const addToast = useToast();
  const [localState, dispatch] = useReducer(reducer, initialState);
  const [verificationType, setVerificationType] = useState({});
  const [consentTypes, setConsentTypes] = useState([]);
  const [validityTypes, setValidityTypes] = useState([]);
  const [allVerificationTypes, setAllVerificationTypes] = useState([]);
  const [allKindOfVerifications, setAllKindOfVerifications] = useState([]);
  const [reports, setReports] = useState([]);
  const [validation, validate] = useDirtyFieldValidation(validator);
  const [filteredUserGroupOptions, setFilteredUserGroupOptions] = useState([]);

  // #region Queries

  const { loading: loadingVerificationType } = useQuery(VERIFICATION_TYPE_QUERY, {
    variables: {
      verificationTypeId: verificationTypeId
    },
    onCompleted: data => {
      setVerificationType(data.verificationType);
    },
    skip: !verificationTypeId
  });

  useQuery(PARTNER_CONSENTS, {
    variables: {
      input: ''
    },
    onCompleted: data => {
      setConsentTypes(data?.partnerConsents || []);
    },
    skip: !verificationTypeId
  });

  useQuery(ALL_VERIFICATION_TYPES_QUERY, {
    onCompleted: data => {
      setAllVerificationTypes(data.allVerificationTypes.filter(x => x.id !== verificationTypeId));
    }
  });

  useQuery(ALL_KIND_OF_VERIFICATIONS_QUERY, {
    onCompleted: data => {
      setAllKindOfVerifications(data.allKindOfVerifications);
    }
  });

  useQuery(VERIFICATION_VALIDITY_TYPES_QUERY, {
    skip: activeService?.route !== Origination.serviceRoute,
    onCompleted: data => {
      setValidityTypes(data.validityTypes);
    }
  });

  useQuery(ALL_REPORTS_QUERY, {
    onCompleted: data => {
      setReports(data.allReports);
    }
  });

  const { data: userGroups, refetch } = useQuery(USER_GROUPS_QUERY);

  // #endregion

  // #region Mutations

  const [updateVerificationType] = useMutation(UPDATE_VERIFICATION_TYPE, {
    onError: () => {
      dispatch({ type: 'OnItemSaveFailed' });
    }
  });

  // #endregion

  // #region Subscriptions

  useSubscription(VERIFICATION_TYPE_UPDATED, {
    onData: ({ data: { data }, client }) => {
      const verificationType = data.verificationTypeUpdated.verificationType;
      addToast(t('VerificationTypes.VerificationTypeUpdated'), 'success');
      dispatch({ type: 'OnItemSaved', verificationType });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedVerificationTypes' });
      client.cache.gc();
    }
  });

  // #endregion

  // #region Memos
  const externalVerificationWithReportId = useMemo(() => {
    if (allKindOfVerifications && allKindOfVerifications.length > 0)
      return allKindOfVerifications.find(x => x.code === EXTERNAL_INTEROGATION_REPORT)?.id;
  }, [allKindOfVerifications]);

  const reportVerificationId = useMemo(() => {
    if (allKindOfVerifications && allKindOfVerifications.length > 0) return allKindOfVerifications.find(x => x.code === REPORT)?.id;
  }, [allKindOfVerifications]);

  const externalVerificationId = useMemo(() => {
    if (allKindOfVerifications && allKindOfVerifications.length > 0)
      return allKindOfVerifications.find(x => x.code === EXTERNAL_INTEROGATION)?.id;
  }, [allKindOfVerifications]);

  const validityTypeNumberOfDaysId = useMemo(() => {
    if (validityTypes && validityTypes.length > 0) return validityTypes.find(x => x.code === ValidityTypeNumberOfDays)?.id;
  }, [validityTypes]);
  // #endregion

  // #region Side effects

  useEffect(() => {
    if (!openVerificationTest) onApplicabilityFormulaForTestChanged(verificationType.applyFormula);
  }, [onApplicabilityFormulaForTestChanged, openVerificationTest, verificationType.applyFormula]);

  useEffect(() => {
    if (!verificationTypeId) dispatch({ type: 'OnItemAdded' });
    else if (verificationType && !loadingVerificationType) dispatch({ type: 'OnInitState', verificationType });
  }, [verificationType, loadingVerificationType, verificationTypeId]);

  useEffect(() => {
    if (localState && localState.item) {
      onChangeTitle(localState.item.verificationName);
    }
  }, [localState, onChangeTitle]);

  useEffect(() => {
    if (!localState || !localState?.item || !localState?.item?.documentTypes) return;
    const verificationType = { ...localState.item, documentTypes: localState.item.documentTypes };
    const input = { ...verificationType, documentTypes: verificationType.documentTypes.map(x => ({ ...x })) };
    validate(input);
  }, [localState, validate, verificationType]);

  useEffect(() => {
    if (!localState || !localState?.item || !localState?.item?.userGroups || !userGroups) return;
    const filteredOptions = userGroups.userGroupsConfig.filter(
      y => !localState.item.userGroups.some(x => x.userGroup.id === y.id || (x.editValue && x.editValue.userGroup.id === y.id))
    );
    setFilteredUserGroupOptions(filteredOptions);
  }, [localState, userGroups, verificationType]);

  // #endregion

  const handleAdd = () => {
    dispatch({
      type: 'OnVerificationTypeUserGroupAdded',
      verificationTypeId
    });
  };

  const handleVerificationTypeUserGroupChanged = (propName, propValue, index) => {
    dispatch({ type: 'OnVerificationTypeUserGroupChanged', propName, propValue, index });
  };

  const handleDelete = index => {
    dispatch({ type: 'OnVerificationTypeUserGroupDelete', index });
  };

  const handleCancel = index => {
    dispatch({ type: 'OnCancelVerificationTypeUserGroup', index });
  };

  const handleEdit = index => {
    dispatch({ type: 'OnEditVerificationTypeUserGroup', index });
  };

  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  const handleSave = useCallback(() => {
    const verificationType = { ...localState.item, documentTypes: localState.item.documentTypes, userGroups: localState.item.userGroups };
    const input = {
      ...verificationType,
      documentTypes: verificationType.documentTypes.map(x => ({ ...x })),
      validityDays: verificationType.validityTypeId === validityTypeNumberOfDaysId ? verificationType.validityDays : null,
      userGroups: verificationType.userGroups
        .map(({ editValue, ...x }) => {
          if (verificationType.fieldBaseSecurity) {
            return {
              verificationTypeId: x.verificationTypeId,
              viewReportYN: editValue ? editValue.viewReportYN : x.viewReportYN,
              viewSummaryYN: editValue ? editValue.viewSummaryYN : x.viewSummaryYN,
              userGroup: {
                id: editValue ? editValue.userGroup.id : x.userGroup.id,
                name: editValue ? editValue.userGroup.name : x.userGroup.name
              }
            };
          } else {
            return null;
          }
        })
        .filter(group => group !== null)
    };

    if (input.userGroups.some(x => !x.userGroup.id)) {
      addToast(t('VerificationTypes.EmptyUserGroup'), 'error');
      return;
    }
    if (!validate(input)) {
      return;
    }
    delete input.isDirty;
    delete input.saving;
    if (input.kindOfVerificationId !== externalVerificationWithReportId && input.kindOfVerificationId !== reportVerificationId)
      input.reportId = null;
    if (!input.triggersVerifications) {
      input.groupId = null;
      input.parentVerificationTypeId = null;
    }
    updateVerificationType({ variables: { input } });
    dispatch({ type: 'OnItemSaveStarted' });
  }, [
    localState.item,
    validityTypeNumberOfDaysId,
    validate,
    externalVerificationWithReportId,
    reportVerificationId,
    updateVerificationType,
    addToast,
    t
  ]);

  return (
    <Card
      icon={Details}
      iconColor='primary'
      actions={
        <IconButton
          onClick={handleSave}
          tooltip={t('SaveChanges')}
          type='save'
          fontSize='medium'
          color='transparent'
          disabled={!localState.item.isDirty || localState.item.saving}
        />
      }
    >
      {loadingVerificationType || !localState.verificationTypeId ? (
        <FakeText lines={10} />
      ) : (
        <>
          <VerificationTypeDetailComponent
            verificationType={localState.item}
            dispatch={dispatch}
            reports={reports}
            allVerificationTypes={allVerificationTypes}
            consentTypes={consentTypes}
            allKindOfVerifications={allKindOfVerifications}
            validityTypes={validityTypes}
            externalVerificationWithReportId={externalVerificationWithReportId}
            externalVerificationId={externalVerificationId}
            validityTypeNumberOfDaysId={validityTypeNumberOfDaysId}
            reportVerificationId={reportVerificationId}
            validation={validation}
          />
          {localState.item.fieldBaseSecurity && (
            <VerificationTypeUserGroupsListComponent
              verificationType={localState.item}
              userGroups={filteredUserGroupOptions}
              allUserGroups={userGroups ? userGroups.userGroupsConfig : []}
              onPropertyChanged={handleVerificationTypeUserGroupChanged}
              onDelete={handleDelete}
              onAdd={handleAdd}
              onCancel={handleCancel}
              onEdit={handleEdit}
              validation={validation}
            />
          )}
        </>
      )}
    </Card>
  );
};

VerificationTypeContainer.propTypes = {
  verificationTypeId: PropTypes.number.isRequired,
  onChangeTitle: PropTypes.func,
  openVerificationTest: PropTypes.bool,
  onApplicabilityFormulaForTestChanged: PropTypes.func
};

export default VerificationTypeContainer;
